import { useState, useRef } from "react";
import { venues } from "../data";
import {
  DAYS, MONTHS, ADMIN_PASSWORD, DEFAULT_CLASS, EMPTY_PRICING,
  formatTime, toLocalDateStr, parseLocalDate, workshopDateRange,
} from "./calendarUtils";


// ─── Password Modal ───────────────────────────────────────
export function PasswordModal({ onSuccess, onClose }) {
  const [pw, setPw]     = useState("");
  const [error, setErr] = useState(false);
  const ref             = useRef(null);

  const submit = () => {
    if (pw === ADMIN_PASSWORD) { onSuccess(); }
    else { setErr(true); setPw(""); setTimeout(() => ref.current?.focus(), 50); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card admin-pw-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Admin Access</div>
        <h2 className="modal-title">Owner Login</h2>
        <p className="admin-pw-hint">Enter your password to manage the schedule.</p>
        <input ref={ref} autoFocus type="password"
          className={`admin-pw-input ${error ? "input-error" : ""}`}
          placeholder="Password" value={pw}
          onChange={e => { setPw(e.target.value); setErr(false); }}
          onKeyDown={e => e.key === "Enter" && submit()} />
        {error && <p className="admin-pw-error">Incorrect password. Try again.</p>}
        <button className="book-btn" onClick={submit}>Enter Admin Mode</button>
      </div>
    </div>
  );
}

// ─── Mini Calendar ────────────────────────────────────────
export function MiniCalendar({ selectedDates = [], onToggleDate }) {
  const today = new Date();
  const [cal, setCal] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year  = cal.getFullYear();
  const month = cal.getMonth();

  const daysInMonth = new Date(year, month+1, 0).getDate();
  const firstDay    = new Date(year, month, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="mini-cal">
      <div className="mini-cal-header">
        <button className="mini-cal-nav" onClick={()=>setCal(new Date(year,month-1,1))}>‹</button>
        <span className="mini-cal-title">{MONTHS[month].slice(0,3)} {year}</span>
        <button className="mini-cal-nav" onClick={()=>setCal(new Date(year,month+1,1))}>›</button>
      </div>
      <div className="mini-cal-grid">
        {DAYS.map(d=><div key={d} className="mini-cal-dow">{d.slice(0,1)}</div>)}
        {cells.map((date,i) => {
          if (!date) return <div key={`e${i}`} />;
          const str    = toLocalDateStr(date);
          const sel    = selectedDates.includes(str);
          const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          return (
            <button key={str}
              className={`mini-cal-day ${sel?"selected":""} ${isPast?"past":""}`}
              onClick={()=>onToggleDate(str)}
            >{date.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Day-of-Week Picker ───────────────────────────────────
export function DayOfWeekPicker({ value, onChange }) {
  return (
    <div className="dow-picker">
      {DAYS.map((d,i) => (
        <button key={d} type="button"
          className={`dow-pill ${value===i?"active":""}`}
          onClick={()=>onChange(i)}
        >{d.slice(0,1)}</button>
      ))}
    </div>
  );
}

// ─── Class Form ───────────────────────────────────────────
export function ClassForm({ initial, venueList, isEdit, onSave, onDelete, onBack }) {
  const isOnlineInit = initial.venue === "venue_online";
  const initTab = initial.isPrivate ? "private" : initial.isHybrid ? "hybrid" : isOnlineInit ? "online" : "inperson";
  const [locTab, setLocTab] = useState(initTab);
  const [form, setForm]          = useState(initial);
  const [confirmDelete, setConf] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const inPersonVenues = venueList.filter(v => v.id !== "venue_online");

 const switchTab = (tab) => {
  setLocTab(tab);
  set("isHybrid", tab === "hybrid");
  set("isPrivate", tab === "private");
  if (tab === "online") set("venue", "venue_online");
  else if (tab === "inperson" || tab === "hybrid") set("venue", inPersonVenues[0]?.id ?? "venue_001");
};

  const valid = form.title.trim() && form.teacher.trim();

  return (
    <div className="form-inner">
      <button className="admin-back-btn" onClick={onBack}>‹ Back</button>
      {isEdit && (
        <p className="recurring-warning">
          ⚠ Changes here affect <strong>every future occurrence</strong>.
          To change a single date, click it on the calendar instead.
        </p>
      )}
      <div className="form-loc-tabs">
        <button className={`form-loc-tab ${locTab==="inperson"?"active":""}`} onClick={()=>switchTab("inperson")}>
          📍 In Person
        </button>
        <button className={`form-loc-tab ${locTab==="hybrid"?"active":""}`} onClick={()=>switchTab("hybrid")}>
          🔀 Hybrid
        </button>
        <button className={`form-loc-tab ${locTab==="online"?"active":""}`} onClick={()=>switchTab("online")}>
          💻 Online
        </button>
        <button className={`form-loc-tab ${locTab==="private"?"active":""}`} onClick={()=>switchTab("private")}>
          🔒 Private
        </button>
      </div>
      {(locTab==="inperson" || locTab==="hybrid") && (
        <div className="form-venue-row">
          {inPersonVenues.length === 1
            ? <span className="venue-single-name">📍 {inPersonVenues[0].name}</span>
            : <select className="form-input" value={form.venue} onChange={e=>set("venue",e.target.value)}>
                {inPersonVenues.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
          }
        </div>
      )}
      <div className="form-section">
        <span className="form-label" style={{display:"block",marginBottom:"0.5rem"}}>Day of Week</span>
        <DayOfWeekPicker value={form.dayOfWeek} onChange={v=>set("dayOfWeek",v)} />
      </div>
      <div className="form-grid" style={{marginTop:"1rem"}}>
        <label className="form-label">Class Title
          <input className="form-input" value={form.title} onChange={e=>set("title",e.target.value)} placeholder="e.g. Iyengar Yoga" />
        </label>
        <label className="form-label">Teacher
          <input className="form-input" value={form.teacher} onChange={e=>set("teacher",e.target.value)} />
        </label>
        <label className="form-label">Time
          <input className="form-input" type="time" value={form.time} onChange={e=>set("time",e.target.value)} />
        </label>
        <label className="form-label">Duration (min)
          <input className="form-input" type="number" min="15" step="15" value={form.duration} onChange={e=>set("duration",Number(e.target.value))} />
        </label>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Price ($)
          <input className="form-input" type="number" min="0" value={form.price} 
          onChange={e=>set("price",Number(e.target.value))} />
        </label>
      </div>
      <div className="form-actions">
        {isEdit && (
          confirmDelete
            ? <><span className="delete-confirm-text">Are you sure?</span>
                <button className="admin-delete-btn" onClick={()=>onDelete(form.id)}>Yes, delete</button>
                <button className="admin-edit-btn" onClick={()=>setConf(false)}>Cancel</button></>
            : <button className="admin-delete-btn" onClick={()=>setConf(true)}>Delete</button>
        )}
        <button className="book-btn" style={{flex:1}} disabled={!valid} onClick={()=>onSave(form)}>
          {isEdit ? "Save Changes" : "Add Class"}
        </button>
      </div>
    </div>
  );
}

// ─── Workshop Form ────────────────────────────────────────
export function WorkshopForm({ initial, venueList, isEdit, onSave, onDelete, onBack }) {
  const [form, setForm]          = useState(() => ({
    ...initial,
    days: (initial.days || []).map(d => ({...d, sessions: (d.sessions||[]).map(s=>({...s}))})),
    pricing: {...(initial.pricing || EMPTY_PRICING)},
  }));
  const [confirmDelete, setConf] = useState(false);

  const setTop   = (k, v) => setForm(f => ({...f, [k]: v}));
  const setPrice = (k, v) => setForm(f => ({...f, pricing: {...f.pricing, [k]: Number(v)}}));

  const toggleDate = (dateStr) => {
    setForm(f => {
      const exists = f.days.find(d => d.date === dateStr);
      if (exists) return {...f, days: f.days.filter(d => d.date !== dateStr)};
      const sorted = [...f.days, { date: dateStr, venue: venueList[0]?.id ?? "venue_001", sessions: [] }]
        .sort((a,b) => a.date.localeCompare(b.date));
      return {...f, days: sorted};
    });
  };

  const setDayField = (di, k, v) =>
    setForm(f => ({...f, days: f.days.map((d,i) => i===di ? {...d,[k]:v} : d)}));

  const addSession = (di) =>
    setForm(f => ({
      ...f,
      days: f.days.map((d,i) => i!==di ? d : {
        ...d, sessions: [...d.sessions, {
          id: `s_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
          label:"", time:"09:00", duration:90, price: f.pricing.perSession
        }]
      })
    }));

  const removeSession = (di, si) =>
    setForm(f => ({...f, days: f.days.map((d,i) => i!==di ? d : {...d, sessions: d.sessions.filter((_,j)=>j!==si)})}));

  const setSession = (di, si, k, v) =>
    setForm(f => ({
      ...f,
      days: f.days.map((d,i) => i!==di ? d : {
        ...d, sessions: d.sessions.map((s,j) => j!==si ? s : {...s,[k]:v})
      })
    }));

  const selectedDates = form.days.map(d => d.date);
  const valid = form.title.trim() && form.days.length > 0 &&
    form.days.every(d => d.date && d.sessions.length > 0 && d.sessions.every(s=>s.label.trim()));

  return (
    <div className="form-inner">
      <button className="admin-back-btn" onClick={onBack}>‹ Back</button>
      <h3 className="form-section-title">{isEdit ? "Edit Workshop" : "New Workshop"}</h3>
      <div className="form-grid" style={{marginBottom:"1rem"}}>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Workshop Title
          <input className="form-input" value={form.title} onChange={e=>setTop("title",e.target.value)} placeholder="e.g. Spring Intensive" />
        </label>
        <label className="form-label">Teacher
          <input className="form-input" value={form.teacher} onChange={e=>setTop("teacher",e.target.value)} />
        </label>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Description
          <textarea className="form-input form-textarea" value={form.description} onChange={e=>setTop("description",e.target.value)} rows={2} />
        </label>
      </div>
      <h4 className="form-sub-title">Pricing</h4>
      <div className="form-grid" style={{marginBottom:"1.25rem"}}>
        <label className="form-label">Per Session ($)
          <input className="form-input" type="number" min="0" value={form.pricing.perSession} onChange={e=>setPrice("perSession",e.target.value)} />
        </label>
        <label className="form-label">Full Day ($)
          <input className="form-input" type="number" min="0" value={form.pricing.fullDay} onChange={e=>setPrice("fullDay",e.target.value)} />
        </label>
        <label className="form-label" style={{gridColumn:"1/-1"}}>Full Workshop ($)
          <input className="form-input" type="number" min="0" value={form.pricing.fullWorkshop} onChange={e=>setPrice("fullWorkshop",e.target.value)} />
        </label>
      </div>
      <h4 className="form-sub-title">Select Workshop Days</h4>
      <p className="form-hint">Tap dates to add or remove them.</p>
      <MiniCalendar selectedDates={selectedDates} onToggleDate={toggleDate} />
      {form.days.length > 0 && (
        <>
          <h4 className="form-sub-title" style={{marginTop:"1.25rem"}}>Sessions per Day</h4>
          {form.days.map((day, di) => {
            const d = parseLocalDate(day.date);
            const label = d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
            return (
              <div key={day.date} className="workshop-day-block">
                <div className="workshop-day-header">
                  <span className="workshop-day-label">{label}</span>
                  <select className="form-input" style={{width:"auto",fontSize:"0.75rem",padding:"0.2rem 0.5rem"}}
                    value={day.venue} onChange={e=>setDayField(di,"venue",e.target.value)}>
                    {venueList.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                {day.sessions.map((sess, si) => (
                  <div key={sess.id} className="session-row">
                    <label className="form-label" style={{flex:2}}>Label
                      <input className="form-input" value={sess.label} onChange={e=>setSession(di,si,"label",e.target.value)} placeholder="Morning" />
                    </label>
                    <label className="form-label">Time
                      <input className="form-input" type="time" value={sess.time} onChange={e=>setSession(di,si,"time",e.target.value)} />
                    </label>
                    <label className="form-label">Min
                      <input className="form-input" type="number" min="15" step="15" value={sess.duration} onChange={e=>setSession(di,si,"duration",Number(e.target.value))} />
                    </label>
                    <label className="form-label">$
                      <input className="form-input" type="number" min="0" value={sess.price} onChange={e=>setSession(di,si,"price",Number(e.target.value))} />
                    </label>
                    <button className="ws-remove-btn session-remove" onClick={()=>removeSession(di,si)}>✕</button>
                  </div>
                ))}
                <button className="admin-add-inline-btn small" onClick={()=>addSession(di)}>+ Add Session</button>
              </div>
            );
          })}
        </>
      )}
      <div className="form-actions" style={{marginTop:"1.25rem"}}>
        {isEdit && (
          confirmDelete
            ? <><span className="delete-confirm-text">Are you sure?</span>
                <button className="admin-delete-btn" onClick={()=>onDelete(form.id)}>Yes, delete</button>
                <button className="admin-edit-btn" onClick={()=>setConf(false)}>Cancel</button></>
            : <button className="admin-delete-btn" onClick={()=>setConf(true)}>Delete</button>
        )}
        <button className="book-btn" style={{flex:1}} disabled={!valid} onClick={()=>onSave(form)}>
          {isEdit ? "Save Workshop" : "Add Workshop"}
        </button>
      </div>
    </div>
  );
}

// ─── Admin Edit Panel ─────────────────────────────────────
export function AdminEditPanel({ classes, workshops, venueList, onClose,
  onSaveClass, onDeleteClass, onSaveWorkshop, onDeleteWorkshop, initialEditClass }) {
  const [tab, setTab]         = useState("classes");
  const [editingClass, setEC] = useState(initialEditClass ?? null);
  const [editingWS, setEW]    = useState(null);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card admin-panel-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Admin — Edit Schedule</div>
        <div className="admin-tabs">
          <button className={`admin-tab ${tab==="classes"?"active":""}`}
            onClick={()=>{ setTab("classes"); setEC(null); setEW(null); }}>Classes</button>
          <button className={`admin-tab ${tab==="workshops"?"active":""}`}
            onClick={()=>{ setTab("workshops"); setEC(null); setEW(null); }}>Workshops</button>
        </div>
        {tab==="classes" && (
          editingClass
            ? <ClassForm
                initial={editingClass==="new" ? {...DEFAULT_CLASS} : editingClass}
                venueList={venueList} isEdit={editingClass!=="new"}
                onSave={f=>{ onSaveClass(f); setEC(null); }}
                onDelete={id=>{ onDeleteClass(id); setEC(null); }}
                onBack={()=>setEC(null)} />
            : <div className="admin-list">
                {classes.map(c=>(
                  <div key={c.id} className="admin-list-row" onClick={()=>setEC(c)}>
                    <span className={`admin-list-dot ${c.isHybrid?"hybrid-dot":c.venue==="venue_online"?"online-dot":"inperson-dot"}`} />
                    <span className="admin-list-title">{DAYS[c.dayOfWeek]} · {formatTime(c.time)}</span>
                    <span className="admin-list-sub">{c.title}</span>
                    <span className="admin-list-chevron">›</span>
                  </div>
                ))}
                <button className="admin-add-inline-btn" onClick={()=>setEC("new")}>+ Add Class</button>
              </div>
        )}
        {tab==="workshops" && (
          editingWS
            ? <WorkshopForm
                initial={editingWS==="new" ? {title:"",teacher:"",description:"",days:[],pricing:{...EMPTY_PRICING}} : editingWS}
                venueList={venueList} isEdit={editingWS!=="new"}
                onSave={f=>{ onSaveWorkshop(f); setEW(null); }}
                onDelete={id=>{ onDeleteWorkshop(id); setEW(null); }}
                onBack={()=>setEW(null)} />
            : <div className="admin-list">
                {workshops.length===0 && <p className="admin-empty">No workshops yet.</p>}
                {workshops.map(w=>{
                  const dates = workshopDateRange(w);
                  return (
                    <div key={w.id} className="admin-list-row" onClick={()=>setEW(w)}>
                      <span className="admin-list-dot workshop-dot" />
                      <span className="admin-list-title">{w.title}</span>
                      <span className="admin-list-sub">
                        {dates[0]}{dates.length>1?` → ${dates[dates.length-1]}`:""}
                        {" · "}{w.days.length} day{w.days.length!==1?"s":""}
                      </span>
                      <span className="admin-list-chevron">›</span>
                    </div>
                  );
                })}
                <button className="admin-add-inline-btn" onClick={()=>setEW("new")}>+ Add Workshop</button>
              </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin Event Modal ────────────────────────────────────
export function AdminEventModal({ event, date, cancelledDates, dateOverrides, venueList,
                           onCancelDate, onRestoreDate, onSaveDateOverride,
                           onEditRecurring, onClose }) {
  const dateStr     = toLocalDateStr(date);
  const overrideKey = `${event.id}__${dateStr}`;
  const cancelled   = cancelledDates.has(overrideKey);
  const override    = dateOverrides.get(overrideKey);
  const effectiveVenue = override?.venue ?? event.venue;
  const venue       = venues.find(v=>v.id===effectiveVenue);
  const isOnline    = effectiveVenue === "venue_online";
  const humanDate   = date.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});

  const [mode, setMode]               = useState("view");
  const [overrideVenue, setOverrideVenue] = useState(effectiveVenue);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-badge badge-admin">Admin — Managing Class</div>
        <h2 className="modal-title">{event.title}</h2>
        <div className="modal-meta">
          <div className="meta-row"><span className="meta-icon">📅</span><span>{humanDate}</span></div>
          <div className="meta-row"><span className="meta-icon">🕐</span><span>{formatTime(event.time)} · {event.duration} min</span></div>
          <div className="meta-row"><span className="meta-icon">👤</span><span>{event.teacher}</span></div>
          <div className="meta-row">
            <span className="meta-icon">📍</span>
            <span>
              {isOnline ? "Online" : venue?.address}
              {override && <span className="override-badge"> · edited for this date</span>}
            </span>
          </div>
          <div className="meta-row"><span className="meta-icon">💰</span><span>${event.price} drop-in</span></div>
        </div>
        {cancelled && <div className="cancelled-notice">⚠ This date is cancelled</div>}
        {mode === "view" && !cancelled && (
          <div className="admin-date-actions">
            <button className="admin-cancel-date-btn" onClick={()=>onCancelDate(event.id,dateStr)}>Cancel This Date</button>
            <button className="admin-edit-btn" onClick={()=>setMode("editDate")}>Edit This Date Only</button>
            <button className="admin-edit-btn outline" onClick={()=>{ onClose(); onEditRecurring(event); }}>Edit Recurring Class</button>
          </div>
        )}
        {cancelled && (
          <div className="form-actions" style={{marginTop:"1.25rem"}}>
            <button className="admin-restore-btn" onClick={()=>onRestoreDate(event.id,dateStr)}>Restore This Date</button>
          </div>
        )}
        {mode === "editDate" && (
          <div className="date-override-form">
            <p className="date-override-hint">
              Changes apply to <strong>{humanDate}</strong> only — the recurring class is unchanged.
            </p>
            <div className="form-loc-tabs" style={{marginBottom:"0.75rem"}}>
              <button className={`form-loc-tab ${overrideVenue!=="venue_online"?"active":""}`}
                onClick={()=>setOverrideVenue(venueList.find(v=>v.id!=="venue_online")?.id ?? "venue_001")}>
                📍 In Person
              </button>
              <button className={`form-loc-tab ${overrideVenue==="venue_online"?"active":""}`}
                onClick={()=>setOverrideVenue("venue_online")}>
                💻 Online
              </button>
            </div>
            {overrideVenue !== "venue_online" && venueList.filter(v=>v.id!=="venue_online").length > 1 && (
              <select className="form-input" value={overrideVenue} onChange={e=>setOverrideVenue(e.target.value)}
                style={{marginBottom:"0.75rem"}}>
                {venueList.filter(v=>v.id!=="venue_online").map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            <div className="form-actions" style={{marginTop:"1rem"}}>
              <button className="admin-edit-btn" onClick={()=>setMode("view")}>Back</button>
              <button className="book-btn" style={{flex:1}} onClick={()=>{
                onSaveDateOverride(event.id, dateStr, { venue: overrideVenue });
                onClose();
              }}>Save for This Date</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}